"use client";

import React, { useEffect, useRef } from "react";
import { Renderer, Program, Mesh, Triangle } from "ogl";

const vertexShader = `
  attribute vec2 uv;
  attribute vec2 position;
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = vec4(position, 0, 1);
  }
`;

const createShader = (
    baseColor: [number, number, number],
    accentColor: [number, number, number],
    thirdColor: [number, number, number]
) => `
  precision highp float;

  uniform float uTime;
  uniform vec3 uResolution;
  varying vec2 vUv;

  void main() {
    float mr = min(uResolution.x, uResolution.y);
    vec2 uv = (vUv.xy * 2.0 - 1.0) * uResolution.xy / mr;

    float d = -uTime * 0.6;
    float a = 0.0;
    
    for (float i = 0.0; i < 12.0; ++i) {
      float wave = sin(i * 0.5) * 0.5 + 0.5;
      a += cos(i - d - a * uv.x) * wave;
      d += sin(uv.y * i + a) * wave;
    }
    
    d += uTime * 0.5;
    
    vec3 col = vec3(${baseColor.join(", ")}) + 
               0.15 * vec3(
                 sin(d * 0.35) * 0.3 + 0.4, 
                 sin(d * 0.38) * 0.28 + 0.38, 
                 sin(d * 0.4) * 0.25 + 0.35
               );
               
    float blueBlend = (sin(d * 0.2) + cos(a * 0.3)) * 0.25 + 0.2;
    float pinkBlend = (cos(d * 0.25) - sin(a * 0.28)) * 0.2 + 0.15;
    
    col = mix(col, vec3(${accentColor.join(", ")}), blueBlend);
    col = mix(col, vec3(${thirdColor.join(", ")}), pinkBlend);

    gl_FragColor = vec4(col, 1.0);
  }
`;

interface NovatrixProps {
    baseColor: [number, number, number];
    accentColor: [number, number, number];
    thirdColor: [number, number, number];
}

const Novatrix: React.FC<NovatrixProps> = ({
    baseColor,
    accentColor,
    thirdColor,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const renderer = new Renderer({
            dpr: Math.min(window.devicePixelRatio, 2),
            antialias: true,
            alpha: true,
        });
        const gl = renderer.gl;

        const handleResize = () => {
            const { width, height } = container.getBoundingClientRect();
            renderer.setSize(width, height);
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        const geometry = new Triangle(gl);

        const program = new Program(gl, {
            vertex: vertexShader,
            fragment: createShader(baseColor, accentColor, thirdColor),
            uniforms: {
                uTime: { value: 0 },
                uResolution: {
                    value: [
                        gl.canvas.width,
                        gl.canvas.height,
                        gl.canvas.width / gl.canvas.height,
                    ],
                },
            },
        });

        const mesh = new Mesh(gl, { geometry, program });

        let rafId: number;

        const animate = (time: number) => {
            rafId = requestAnimationFrame(animate);
            program.uniforms.uTime.value = time * 0.001;
            renderer.render({ scene: mesh });
        };

        container.appendChild(gl.canvas);
        rafId = requestAnimationFrame(animate);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener("resize", handleResize);
            if (container.contains(gl.canvas)) {
                container.removeChild(gl.canvas);
            }
            gl.getExtension("WEBGL_lose_context")?.loseContext();
            geometry.remove();
            program.remove();
        };
    }, [baseColor, accentColor, thirdColor]);

    return <div ref={containerRef} className="h-full w-full bg-transparent" />;
};

const DocuMateBackground: React.FC = () => {
    // Dark Blue/Black Base
    const baseColor: [number, number, number] = [0.02, 0.05, 0.1];
    // Vibrant Blue Accent
    const accentColor: [number, number, number] = [0.2, 0.4, 0.8];
    // White Third Color
    const thirdColor: [number, number, number] = [0.9, 0.95, 1.0];

    return (
        <div className="absolute inset-0 h-full w-full">
            <Novatrix
                baseColor={baseColor}
                accentColor={accentColor}
                thirdColor={thirdColor}
            />
        </div>
    );
};

export default DocuMateBackground;
